import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { Avatar, Badge as antBadge, Card, message, Modal, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  ExclamationCircleOutlined,
  LoadingOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useUserCountry } from '@hooks/shared/useUserCountry';
import { getMainLayout } from '@layouts/MainLayout';
import {
  PhotosCommandApi,
  PhotosQueryApi,
  UsersQueryApi,
} from '@lib/api/endpoints';
import {
  setUserAppearance,
  setUserProfile,
  setUserProfileInfo,
} from '@lib/redux.lib/slices/appSlice';
import { RootState, useAppDispatch } from '@lib/redux.lib/store';
import { EverysetUser } from '@lib/types/user.types';
import { getDisplayLocation } from '@lib/utils/location';
import { getDisplayNumber } from '@lib/utils/stringHelper';
import theme from '@styles/theme';

import BasketBall from '../../svg/Basketball.svg';
import Camera from '../../svg/Camera.svg';
import Car from '../../svg/Car.svg';
import circleInfo from '../../svg/Circle-info.svg';
import Email from '../../svg/Emails, Letter, Mail.svg';
import MagicWind from '../../svg/Magic wand.svg';
import Phone from '../../svg/Phone.svg';
import Pin from '../../svg/Pin, Location.svg';
import RightToWorkIcon from '../../svg/RightToWorkIcon.svg';
import Tshirt from '../../svg/t-shirt.svg';
import view from '../../svg/View.svg';

const ProfileIndex = (): JSX.Element => {
  const { isUkUser } = useUserCountry();

  const router = useRouter();
  const appDispatch = useAppDispatch();
  const [contactIncomplete, setContactIncomplete] = useState(false);
  const [wardrobeIncomplete, setWardrobeIncomplete] = useState(false);
  const [generalIncomplete, setGeneralIncomplete] = useState(false);
  const [appearanceIncomplete, setAppearanceIncomplete] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [userInfoDatas, setUserInfoDatas] = useState<UserProfile>();
  const [user, setUser] = useState<EverysetUser>();
  const [loading, setLoading] = useState(true);

  const [photos, setPhotos] = useState<UserPhoto[]>([]);

  const userId = useSelector((state: RootState) => state.app.user.profile?.id);

  const profilePhotoUrl = user?.profile_photo.url;

  const userData = useSelector((state: RootState) => state.app.user.profile);
  const userAppearance = useSelector(
    (state: RootState) => state.app.user?.UserAppearance
  );
  const handlePhotoClick = (photo: UserPhoto) => {
    setFormData({
      PhotoId: photo.id,
      user_id: photo.user_id,
      profile_pic_user_id: photo.user_id,
      photo: photo.photo,
    });

    handlePhotoSelect();
  };

  useEffect(() => {
    const getUserInfo = async () => {
      try {
        const photos = await PhotosQueryApi.getUserPhotos(userId);

        setPhotos(photos);
        setLoading(false);
        const UserInfoDatanew = await UsersQueryApi.getUserProfile(userId);
        appDispatch(setUserProfileInfo(UserInfoDatanew));

        const UserAppearance = await UsersQueryApi.getUserAppearance(userId);
        appDispatch(setUserAppearance(UserAppearance));
        const userData = await UsersQueryApi.getUser(userId);
        appDispatch(setUserProfile(userData));
        setUser(userData);

        setUserInfoDatas(UserInfoDatanew);
      } catch (error) {
        console.error('error while api:', error);
      }
    };
    getUserInfo();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleButtonClick = (path) => {
    router.push(path);
  };

  const [formData, setFormData] = useState({
    PhotoId: null,
    user_id: null,
    profile_pic_user_id: null,
    photo: '',
  });

  const ApiForm = useMemo(
    () => ({
      user_id: formData.user_id,
      profile_pic_user_id: formData.profile_pic_user_id,
      photo: formData.photo,
    }),
    [formData.user_id, formData.profile_pic_user_id, formData.photo]
  );

  useEffect(() => {
    const updateProfilePicAndUserData = async () => {
      if (
        formData.PhotoId !== null ||
        formData.user_id !== null ||
        formData.profile_pic_user_id !== null
      ) {
        try {
          await PhotosCommandApi.updateProfilePic(formData.PhotoId, ApiForm);
          message.success('Profile Successfully Saved');
          router.push('/profile');
          // Handle success after updating user general info
        } catch (error) {
          message.error('Error');
          // Handle error during updating user general info
        }
      }

      const userData = await UsersQueryApi.getUser(userId);
      appDispatch(setUserProfile(userData));
      setUser(userData);
    };

    updateProfilePicAndUserData();
  }, [formData, userId, appDispatch, ApiForm, router]); // Include all dependencies of the effect

  const handlePhotoSelect = () => {
    setIsModalVisible(false);
    router.push('/profile');
  };
  const handleChangeProfilePic = () => {
    setIsModalVisible(true);
  };

  const isMobileDevice = window.innerWidth <= 768;
  const isSmallMobileDevice = window.innerWidth <= 414;

  const ListItems = isUkUser
    ? [
        {
          name: 'General Information',
          src: circleInfo.src,
          path: 'GeneralInformation',
          show: generalIncomplete,
        },
        {
          name: 'Appearance & Measurements',
          src: view.src,
          path: 'Appearance',
          show: appearanceIncomplete,
        },
        {
          name: 'Skills',
          src: MagicWind.src,
          path: 'Skills',
        },
        {
          name: 'Props & Uniforms',
          src: BasketBall.src,
          path: 'Props',
        },
        {
          name: 'Work Documents',
          src: RightToWorkIcon.src,
          path: 'Documents',
        },
        {
          name: 'Transportation & Vehicles',
          src: Car.src,
          path: 'Vehicles',
        },
      ]
    : [
        {
          name: 'General Information',
          src: circleInfo.src,
          path: 'GeneralInformation',
          show: generalIncomplete,
        },
        {
          name: 'Contact',
          src: Phone.src,
          path: 'Contact',
          show: contactIncomplete,
        },
        {
          name: 'Measurements',
          src: Tshirt.src,
          path: 'Measurements',
          show: wardrobeIncomplete,
        },
        {
          name: 'Appearance',
          src: view.src,
          path: 'Appearance',
          show: appearanceIncomplete,
        },
        {
          name: 'Skills',
          src: MagicWind.src,
          path: 'Skills',
        },
        {
          name: 'Props & Wardrobe',
          src: BasketBall.src,
          path: 'Props',
        },
        { name: 'Transportation & Vehicles', src: Car.src, path: 'Vehicles' },
      ];

  // If not a mobile device, rearrange the ListItems as desired
  if (!isMobileDevice) {
    const customOrder = isUkUser
      ? [
          'General Information',
          'Skills',
          'Transportation & Vehicles',
          'Appearance & Measurements',
          'Props & Uniforms',
          'Work Documents',
        ]
      : [
          'General Information',
          'Measurements',
          'Skills',
          'Transportation & Vehicles',
          'Contact',
          'Appearance',
          'Props & Wardrobe',
        ];

    ListItems.sort((a, b) => {
      return customOrder.indexOf(a.name) - customOrder.indexOf(b.name);
    });
  }

  const mobileItems = [...ListItems];
  mobileItems.splice(3, 0, {
    name: 'Photos',
    src: Camera.src,
    path: '/Photos',
  });
  const columnOneItems = isUkUser
    ? ListItems.slice(0, 3)
    : ListItems.slice(0, 4);
  const columnTwoItems = isUkUser
    ? ListItems.slice(3, 6)
    : ListItems.slice(4, 8);
  const filteredPhoto = photos?.filter((photo) => photo.profile_pic_user_id);
  useEffect(() => {
    if (isUkUser) {
      if (
        isGeneralInfoIncompleteForUkUser(userInfoDatas, userAppearance, user)
      ) {
        setGeneralIncomplete(true);
      } else {
        setGeneralIncomplete(false);
      }
    } else {
      if (
        userInfoDatas?.first_name === '' ||
        userInfoDatas?.first_name === null ||
        userInfoDatas?.last_name === '' ||
        userInfoDatas?.last_name === null ||
        userInfoDatas?.union_status === '' ||
        userInfoDatas?.union_status === null
      ) {
        setGeneralIncomplete(true);
      }
    }

    if (
      userInfoDatas?.email === '' ||
      userInfoDatas?.email === null ||
      userData?.primary_phone.phone === '' ||
      userData?.primary_phone.phone === null ||
      userInfoDatas?.about.address.street === '' ||
      userInfoDatas?.about.address.street === null ||
      userInfoDatas?.about.address.city === '' ||
      userInfoDatas?.about.address.city === null ||
      userInfoDatas?.about.address.state === '' ||
      userInfoDatas?.about.address.state === null ||
      userInfoDatas?.about.address.zip === '' ||
      userInfoDatas?.about.address.zip === null ||
      userInfoDatas?.about.residential_address.street === '' ||
      userInfoDatas?.about.residential_address.street === null ||
      userInfoDatas?.about.residential_address.city === '' ||
      userInfoDatas?.about.residential_address.city === null ||
      userInfoDatas?.about.residential_address.state === '' ||
      userInfoDatas?.about.residential_address.state === null ||
      userInfoDatas?.about.residential_address.zip === '' ||
      userInfoDatas?.about.residential_address.zip === null
    ) {
      setContactIncomplete(true);
    }

    if (isUkUser) {
      if (
        userInfoDatas?.about.gender === null ||
        userInfoDatas?.about.height === null ||
        userInfoDatas?.about.height === 0 ||
        userInfoDatas?.about.primary_ethnicity === null ||
        userInfoDatas?.about.body_type === null ||
        userAppearance?.hair_length === null ||
        userAppearance?.hair_color === null ||
        userAppearance?.eye_color === null
      ) {
        setAppearanceIncomplete(true);
      } else {
        setAppearanceIncomplete(false);
      }
    } else {
      if (
        userInfoDatas?.about.gender === null ||
        userInfoDatas?.about.height === null ||
        userInfoDatas?.about.height === 0 ||
        userInfoDatas?.about.weight === null ||
        userInfoDatas?.about.weight === 0 ||
        userInfoDatas?.about.primary_ethnicity === null ||
        userInfoDatas?.about.body_type === null ||
        userAppearance?.hair_length === null ||
        userAppearance?.hair_color === null ||
        userAppearance?.eye_color === null
      ) {
        setAppearanceIncomplete(true);
      }
    }
  }, [userAppearance, userInfoDatas, userData, isUkUser, user]);

  useEffect(() => {
    if (userInfoDatas && !isUkUser) {
      const isMinor = userInfoDatas?.about.birthday !== null;
      const isAgeRangeComplete =
        userInfoDatas?.about.min_portrayable_age !== null &&
        userInfoDatas?.about.max_portrayable_age !== null;

      if (isMinor || isAgeRangeComplete) {
        setGeneralIncomplete(false);
      } else {
        setGeneralIncomplete(true);
      }
    }
  }, [userInfoDatas, isUkUser]);

  useEffect(() => {
    if (userInfoDatas && userInfoDatas.about && userInfoDatas.about.gender) {
      const isGenderIncomplete = () => {
        const { gender } = userInfoDatas.about;
        const requiredFields = [];

        switch (gender) {
          case 'Male':
          case 'Transgender Male':
            requiredFields.push(
              'chest_bust',
              'neck',
              'sleeve',
              'waist',
              'inseam',
              'shirt_size',
              'shoe_size',
              'hat'
            );
            break;
          case 'Female':
          case 'Transgender Female':
            requiredFields.push(
              'dress',
              'hip',
              'waist',
              'pants',
              'bust',
              'cup',
              'shirt_size',
              'shoe_size',
              'hat'
            );
            break;
          case 'Non-Binary':
            requiredFields.push(
              'dress',
              'chest_bust',
              'neck',
              'sleeve',
              'hip',
              'waist',
              'inseam',
              'pants',
              'bust',
              'cup',
              'shirt_size',
              'shoe_size',
              'hat'
            );
            break;
          default:
            break;
        }

        return requiredFields.every(
          (field) => userAppearance?.[field] !== null
        );
      };

      setWardrobeIncomplete(!isGenderIncomplete());
    }
  }, [userAppearance, userInfoDatas]);

  const sortedPhotos = photos.sort((a, b) => {
    // Compare the presence of profile_pic_User_id
    if (a.profile_pic_user_id && !b.profile_pic_user_id) {
      return -1; // a comes first
    } else if (!a.profile_pic_user_id && b.profile_pic_user_id) {
      return 1; // b comes first
    } else {
      return 0; // No preference, maintain order
    }
  });

  const location = getDisplayLocation(
    userInfoDatas?.about?.residential_address,
    isUkUser
  );

  const userPhone = getDisplayNumber(user?.primary_phone?.phone, isUkUser);

  return (
    <div
      style={{
        backgroundColor: isMobileDevice ? '#F4F4F4' : 'white',
        display: 'flex',
        flexDirection: 'column',
        margin: 0,
        marginLeft: '0px',
        width: isSmallMobileDevice ? '414px' : '100%',
      }}
    >
      <div
        style={{
          margin: 0,
          minHeight: 280,
          padding: isMobileDevice ? '0px' : '24px',
        }}
      >
        <Title
          style={{
            whiteSpace: 'nowrap',
            paddingLeft: isMobileDevice ? '15px' : '0px',
          }}
        >
          {isMobileDevice
            ? `${userInfoDatas?.first_name} ${userInfoDatas?.last_name} ${
                userInfoDatas?.stage_name
                  ? `(${userInfoDatas?.stage_name})`
                  : ''
              }`
            : 'My Profile'}
        </Title>

        <ProfileWrapper>
          {filteredPhoto[0]?.photo ? (
            <ImageWrapper>
              <RoundedProfilePic
                color={theme.palette.primary[0]}
                style={{
                  cursor: 'pointer',
                }}
              >
                <img
                  alt='profile-picture'
                  src={profilePhotoUrl}
                  onClick={handleChangeProfilePic}
                />
              </RoundedProfilePic>
            </ImageWrapper>
          ) : loading ? (
            <LoadingOutlined
              style={{
                fontSize: 24,
                width: '120px',
                height: '120px',
                color: theme.palette.primary[0],
                alignItems: 'center',
                justifyContent: 'center',
              }}
              spin
            />
          ) : (
            <Avatar
              style={{
                width: '120px',
                height: '120px',
                padding: '40px',
                fontSize: '100px',
                background: theme.palette.primary[0],
                cursor: 'pointer',
              }}
              onClick={handleChangeProfilePic}
              size='large'
            >
              {userInfoDatas?.first_name?.charAt(0)}
              {userInfoDatas?.last_name?.charAt(0)}
            </Avatar>
          )}

          <ProfileDetails>
            <div>
              {!isMobileDevice && (
                <Name>
                  {userInfoDatas?.first_name}
                  {userInfoDatas?.last_name &&
                    ` ${userInfoDatas?.last_name}`}{' '}
                  {userInfoDatas?.stage_name &&
                    `(${
                      userInfoDatas?.stage_name ? userInfoDatas?.stage_name : ''
                    })`}
                </Name>
              )}
              <Badge
                count={
                  user?.union_status === 'sag-aftra'
                    ? 'SAG-AFTRA'
                    : user?.union_status === 'sag-eligible'
                    ? 'SAG-Eligible'
                    : user?.union_status === 'ficore'
                    ? 'FiCore'
                    : user?.union_status === 'must-join'
                    ? 'Must Join'
                    : user?.union_status
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 'fit-content',
                  height: '27px',
                  color: '#202020',
                  fontWeight: '400',
                  backgroundColor:
                    user?.union_status === 'sag-aftra' ||
                    user?.union_status === 'ficore'
                      ? '#FBF5C2'
                      : '#E4EDFC',

                  borderRadius: '4px',
                  fontSize: isMobileDevice ? '14px' : '12px',
                  textTransform: 'capitalize',
                  lineHeight: '25px',
                  borderColor:
                    user?.union_status === 'sag-aftra' ||
                    user?.union_status === 'ficore'
                      ? '#FBF5C2'
                      : '#E4EDFC',
                  padding: '6px 8px',
                }}
              />
            </div>
            <StyledText>
              <IconWrapper
                style={{ fontSize: isMobileDevice ? '14px' : '12px' }}
              >
                <ImageIcon src={Pin.src} />
                {location}
              </IconWrapper>
            </StyledText>
            <StyledText>
              <IconWrapper
                style={{ fontSize: isMobileDevice ? '14px' : '12px' }}
              >
                <ImageIcon src={Phone.src} />
                {userPhone}
              </IconWrapper>
            </StyledText>
            <StyledText>
              <IconWrapper
                style={{ fontSize: isMobileDevice ? '14px' : '12px' }}
              >
                <ImageIcon src={Email.src} />
                {userInfoDatas?.email}
              </IconWrapper>
            </StyledText>
          </ProfileDetails>
        </ProfileWrapper>

        <a onClick={handleChangeProfilePic}>
          <ChangeProfile
            style={{
              paddingLeft: isMobileDevice ? '18px' : '6px',
              width: isMobileDevice ? '250px' : '120px',
              fontSize: isMobileDevice ? '14px' : '12px',
            }}
          >
            Change Profile Pic
          </ChangeProfile>
        </a>
        <CardContainer>
          {!isMobileDevice ? (
            <>
              <Column>
                {columnOneItems.map((item, idx) => (
                  <StyledCard
                    onClick={() => handleButtonClick(`/profile/${item.path}`)}
                    key={idx}
                  >
                    <IconWrapper>
                      <ImageIcon src={item.src} />
                      <span>{item.name}</span>
                    </IconWrapper>
                    {item.show && (
                      <Tooltip title='Section Incomplete'>
                        <InfoIcon
                          className='question-icon'
                          style={{ color: 'red' }}
                        />
                      </Tooltip>
                    )}
                  </StyledCard>
                ))}
              </Column>
              <Column>
                {columnTwoItems.map((item, idx) => (
                  <StyledCard
                    onClick={() => handleButtonClick(`/profile/${item.path}`)}
                    key={idx}
                  >
                    <IconWrapper>
                      <ImageIcon src={item.src} />
                      <span>{item.name}</span>
                    </IconWrapper>
                    {item.show && (
                      <Tooltip title='Section Incomplete'>
                        <InfoIcon
                          className='question-icon'
                          style={{ color: 'red' }}
                        />
                      </Tooltip>
                    )}
                  </StyledCard>
                ))}
              </Column>
            </>
          ) : (
            // <List>
            // {ListItems.map((item) => (
            // <><List.Item key={item.name}>
            // <IconWrapper>
            // <div>
            // <ImageIcon src={item.src} />
            // <span>{item.name}</span>
            // <ArrowIcon />
            // </div>
            // </IconWrapper>
            // </List.Item>
            // </>
            // ))}
            // </List>
            <CardContainer style={{ backgroundColor: '#F4F4F4' }}>
              <CardColumn>
                {mobileItems.map((item, idx) => (
                  <StyledCardMobile
                    onClick={() => handleButtonClick(`/profile/${item.path}`)}
                    key={idx}
                  >
                    <IconWrapper>
                      <ImageIcon src={item.src} />
                      <span>{item.name}</span>
                    </IconWrapper>
                    <ArrowIcon />
                  </StyledCardMobile>
                ))}
              </CardColumn>
            </CardContainer>
          )}
          {!isMobileDevice && (
            <>
              <Column style={{ marginTop: '12px' }}>
                <StyledLinkPhoto>Photos</StyledLinkPhoto>
                {sortedPhotos.slice(0, 1).map((item, index) => (
                  <PhotoWrapper key={index}>
                    <img src={item.photo} alt={`Photo ${index * 2 + 1}`} />
                  </PhotoWrapper>
                ))}
                {sortedPhotos.slice(2, 3).map((item, index) => (
                  <PhotoWrapper key={index}>
                    <img src={item.photo} alt={`Photo ${index * 2 + 5}`} />
                  </PhotoWrapper>
                ))}
              </Column>
              <Column style={{ marginTop: '12px' }}>
                <StyledLink
                  style={{
                    alignSelf: 'flex-end',
                    whiteSpace: 'nowrap',
                    marginRight: '35px',
                    color: theme.palette.primary[0],
                  }}
                  onClick={() => handleButtonClick('/profile/Photos')}
                >
                  View Photos
                </StyledLink>
                {sortedPhotos.slice(1, 2).map((item, index) => (
                  <PhotoWrapper key={index}>
                    <img src={item.photo} alt={`Photo ${index * 2 + 2}`} />
                  </PhotoWrapper>
                ))}
                {sortedPhotos.slice(3, 4).map((item, index) => (
                  <PhotoWrapper key={index}>
                    <img src={item.photo} alt={`Photo ${index * 2 + 6}`} />
                  </PhotoWrapper>
                ))}
              </Column>
            </>
          )}
        </CardContainer>
        <Modal
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
        >
          <ModalTitle>Select New Profile Picture</ModalTitle>
          <ModalContent>
            {sortedPhotos.slice(0, 4).map((item, index) => (
              <ProfilePicOptions
                key={index}
                onClick={() => handlePhotoClick(item)}
              >
                <img src={item.photo} alt={`Photo ${index + 1}`} />
              </ProfilePicOptions>
            ))}
          </ModalContent>
        </Modal>
      </div>
    </div>
  );
};

ProfileIndex.getLayout = getMainLayout;

export default ProfileIndex;
const Title = styled.h1`
  width: 107px;
  height: 27px;
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 27px;
  color: #202020;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

const ProfileWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
`;

const ImageWrapper = styled.div`
  margin-right: 20px;
  width: 120px;
  height: 120px;
  @media (max-width: 576px) {
    margin-right: 20px;
    padding-left: 20px !important;
  }
`;
const InfoIcon = styled(ExclamationCircleOutlined)`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
`;

const ProfileDetails = styled.div``;

const ChangeProfile = styled.a`
  height: 15px;
  margin-top: 8px;
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 15px;
  color: #202020;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.palette.primary[0]};
  }
`;

const StyledLink = styled.a`
  width: 41px;
  height: 15px;
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 15px;
  color: #202020;
  cursor: pointer;
  margin-bottom: 10px;
`;
const StyledLinkPhoto = styled.a`
  width: 41px;
  height: 15px;
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 15px;
  color: #202020;
  margin-bottom: 10px;
`;
const Badge = styled(antBadge)`
  width: 78px;
  height: 27px;
  border-radius: 4px;
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 15px;
  color: #202020;
  gap: 10px;
  justify-content: center;
  align-items: center;
`;

const Name = styled.span`
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
  color: #000000;
  margin-right: 10px;
`;

const CardContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  // gap: 20px;
`;

const StyledCard = styled(Card)`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 334px;
  height: 60px;
  background: #fdfdfd;
  border: 1px solid #d8d8d8;
  border-radius: 4px;
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  color: #202020;
  cursor: pointer;
  cursor: pointer;
  margin-top: 15px;
  @media (max-width: 968px) {
    width: 100%; /* Responsive width for screens up to 968px */
  }
`;
const StyledText = styled.div`
  margin-top: 10px;
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 15px;
`;
const IconWrapper = styled.div`
  display: flex; /* Add flex display */
  align-items: center; /* Align items vertically */
  @media (min-width: 576px) {
    align-items: none;
  }
`;
const ImageIcon = styled.img`
  margin-right: 8px;
  width: 24px;
  height: 24px;
`;
const PhotoWrapper = styled.div`
  border-radius: 4px;
  overflow: hidden;
  width: 183px;
  height: 183px;
  margin-bottom: 10px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;
const ArrowIcon = styled(RightOutlined)`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
`;

const StyledCardMobile = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 310px;
  height: 72px;
  background: #fdfdfd;
  border-top: 1px solid #d8d8d8;
  border-bottom: 1px solid #d8d8d8;
  flex: none;
  order: 0;
  flex-grow: 0;
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  color: #202020;
  @media (max-width: 768px) {
    width: 385px;
  }
  @media (max-width: 450px) {
    width: 414px;
  }
`;

const CardColumn = styled(Column)`
  align-items: stretch;
  margin-bottom: 10px;
`;

const RoundedProfilePic = styled.div`
  img {
    width: 120px;
    height: 120px;
    @media (max-width: 576px) {
      width: 100px;
      height: 100px;
    }
    border-radius: 50%;
    max-width: 100%;
    max-height: 100%;
    object-fit: cover;
  }
`;

const ModalTitle = styled.div`
  font-family: 'Inter';
  font-weight: 600;
  font-size: 17px;
  line-height: 21px;
  margin-bottom: 5%;
`;

const ModalContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 10px;
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;
const ProfilePicOptions = styled.div`
  cursor: pointer;
  width: 231px;
  height: 231px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: cover;
  }
`;

const isGeneralInfoIncompleteForUkUser = (
  userInfoDatas: UserProfile,
  userAppearance: UserAppearance,
  user: EverysetUser
) => {
  return !!(
    userInfoDatas?.first_name === '' ||
    userInfoDatas?.first_name === null ||
    userInfoDatas?.last_name === '' ||
    userInfoDatas?.last_name === null ||
    userAppearance?.birthday === null ||
    userAppearance?.birthday === '' ||
    userInfoDatas?.email === '' ||
    userInfoDatas?.email === null ||
    user?.primary_phone?.phone === '' ||
    user?.primary_phone?.phone === null ||
    userInfoDatas?.about?.residential_address?.street === '' ||
    userInfoDatas?.about?.residential_address?.street === null ||
    userInfoDatas?.about?.residential_address?.city === '' ||
    userInfoDatas?.about?.residential_address?.city === null ||
    userInfoDatas?.about?.residential_address?.zip === '' ||
    userInfoDatas?.about?.residential_address?.zip === null ||
    userInfoDatas?.about?.residential_address?.county === '' ||
    userInfoDatas?.about?.residential_address?.county === null ||
    userInfoDatas?.about?.residential_address?.country === '' ||
    userInfoDatas?.about?.residential_address?.country === null
  );
};
